import { FormEvent, useEffect, useState } from 'react';
import type { ProductSize } from '@rosita/shared';
import { api, extractError } from '../../../lib/api';
import { useToast } from '../../../components/admin/Toast';
import { dangerBtnText, inputClass, primaryBtn, secondaryBtn } from './shared';

interface Draft {
  label: string;
  sortOrder: number;
  isActive: boolean;
}

export default function SizesTab(): JSX.Element {
  const toast = useToast();
  const [items, setItems] = useState<ProductSize[]>([]);
  const [label, setLabel] = useState('');
  const [sortOrder, setSortOrder] = useState(0);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);

  const load = async (): Promise<void> => {
    const response = await api.get<ProductSize[]>('/admin/sizes');
    setItems(response.data);
  };

  useEffect(() => {
    void load();
  }, []);

  const handleCreate = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    try {
      await api.post('/admin/sizes', { label, sortOrder, isActive: true });
      setLabel('');
      setSortOrder(0);
      toast.success('Tamanho criado.');
      await load();
    } catch (err) {
      toast.error(extractError(err, 'Falha ao criar tamanho'));
    }
  };

  const startEdit = (s: ProductSize): void => {
    setEditingId(s.id);
    setDraft({ label: s.label, sortOrder: s.sortOrder, isActive: s.isActive });
  };

  const saveEdit = async (): Promise<void> => {
    if (!editingId || !draft) return;
    try {
      await api.put(`/admin/sizes/${editingId}`, draft);
      setEditingId(null);
      setDraft(null);
      toast.success('Tamanho atualizado.');
      await load();
    } catch (err) {
      toast.error(extractError(err, 'Falha ao atualizar tamanho'));
    }
  };

  const remove = async (s: ProductSize): Promise<void> => {
    if (!window.confirm(`Apagar tamanho "${s.label}"? As variantes existentes mantêm o valor.`)) {
      return;
    }
    try {
      await api.delete(`/admin/sizes/${s.id}`);
      toast.success('Tamanho eliminado.');
      await load();
    } catch (err) {
      toast.error(extractError(err, 'Falha ao apagar tamanho'));
    }
  };

  return (
    <section>
      <form
        onSubmit={(e) => {
          void handleCreate(e);
        }}
        className="mb-6 grid grid-cols-1 gap-2 md:grid-cols-[1fr_100px_auto]"
      >
        <input
          placeholder="Rótulo (ex.: M, XL, 42)"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          required
          className={inputClass}
        />
        <input
          type="number"
          value={sortOrder}
          onChange={(e) => setSortOrder(Number(e.target.value))}
          className={inputClass}
          placeholder="Ordem"
        />
        <button type="submit" className={primaryBtn}>
          Criar
        </button>
      </form>

      <ul className="overflow-hidden rounded-lg border border-admin-border bg-admin-surface">
        {items.length === 0 ? (
          <li className="p-4 text-sm text-admin-muted">Sem tamanhos.</li>
        ) : null}
        {items.map((s) => {
          const isEditing = editingId === s.id;
          return (
            <li
              key={s.id}
              className="flex flex-wrap items-center gap-3 border-b border-admin-border p-3 last:border-b-0"
            >
              {isEditing && draft ? (
                <>
                  <input
                    value={draft.label}
                    onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                    className={inputClass}
                  />
                  <input
                    type="number"
                    value={draft.sortOrder}
                    onChange={(e) => setDraft({ ...draft, sortOrder: Number(e.target.value) })}
                    className={`${inputClass} w-24`}
                  />
                  <label className="flex items-center gap-1 text-sm">
                    <input
                      type="checkbox"
                      checked={draft.isActive}
                      onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })}
                    />
                    Ativo
                  </label>
                  <button type="button" onClick={() => void saveEdit()} className={primaryBtn}>
                    Guardar
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingId(null);
                      setDraft(null);
                    }}
                    className={secondaryBtn}
                  >
                    Cancelar
                  </button>
                </>
              ) : (
                <>
                  <span className="flex-1 font-medium">{s.label}</span>
                  <span className="text-xs text-admin-muted">ordem {s.sortOrder}</span>
                  {!s.isActive ? (
                    <span className="rounded bg-admin-surface-alt px-2 py-0.5 text-xs text-admin-muted">
                      inativo
                    </span>
                  ) : null}
                  <button type="button" onClick={() => startEdit(s)} className={secondaryBtn}>
                    Editar
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      void remove(s);
                    }}
                    className={dangerBtnText}
                  >
                    Apagar
                  </button>
                </>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
